// Currency formatting
export const formatCurrency = (amount, currency = "₦") => {
  if (!amount && amount !== 0) return "₦0";
  
  // Handle comma-separated strings like "19,073.18"
  let cleanAmount = amount;
  if (typeof amount === "string") {
    cleanAmount = amount.replace(/,/g, '');
  }
  
  const numAmount = parseFloat(cleanAmount);
  
  if (isNaN(numAmount)) return "₦0";
  
  if (numAmount >= 1000000) {
    return `${currency}${(numAmount / 1000000).toFixed(1)}M`;
  } else if (numAmount >= 1000) {
    return `${currency}${(numAmount / 1000).toFixed(1)}K`;
  } else {
    return `${currency}${numAmount.toLocaleString()}`;
  }
};

// Charges-specific currency formatting (shows full amount)
export const formatChargesCurrency = (amount, currency = "₦") => {
  if (!amount && amount !== 0) return "₦0";
  
  // Handle comma-separated strings like "19,073.18"
  let cleanAmount = amount;
  if (typeof amount === "string") {
    cleanAmount = amount.replace(/,/g, '');
  }
  
  const numAmount = parseFloat(cleanAmount);
  
  if (isNaN(numAmount)) return "₦0";
  
  // Always show full amount for charges
  return `${currency}${numAmount.toLocaleString()}`;
};

// Percentage formatting
export const formatPercentage = (value, decimals = 1) => {
  if (!value && value !== 0) return "0%";
  return `${parseFloat(value).toFixed(decimals)}%`;
};

// Date formatting
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Chart data preparation
export const prepareChartData = (data, type = "line") => {
  if (!data || !Array.isArray(data)) return { labels: [], datasets: [] };

  const labels = data.map(item => formatDate(item.date));
  const values = data.map(item => item.count || item.total_amount || 0);

  return {
    labels,
    datasets: [
      {
        label: type === "line" ? "Transactions" : "Amount",
        data: values,
        borderColor: "#7352FF",
        backgroundColor: "rgba(115, 82, 255, 0.1)",
        tension: 0.4,
        fill: type === "area",
      },
    ],
  };
};

// Bank analysis chart data
export const prepareBankChartData = (bankData) => {
  if (!bankData || !Array.isArray(bankData)) return { labels: [], datasets: [] };

  const labels = bankData.map(bank => bank.payer_bank);
  const amounts = bankData.map(bank => bank.total_amount);

  return {
    labels,
    datasets: [
      {
        label: "Transaction Volume",
        data: amounts,
        backgroundColor: [
          "#7352FF",
          "#FF6B6B",
          "#4ECDC4",
          "#45B7D1",
          "#96CEB4",
        ],
        borderWidth: 0,
      },
    ],
  };
};

// Calculate growth rate
export const calculateGrowthRate = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

// Calculate success rate
export const calculateSuccessRate = (successful, total) => {
  if (!total || total === 0) return 0;
  return (successful / total) * 100;
};

// Get trend indicator
export const getTrendIndicator = (value) => {
  if (value > 0) return { direction: "up", color: "text-green-500" };
  if (value < 0) return { direction: "down", color: "text-red-500" };
  return { direction: "neutral", color: "text-gray-500" };
};

// Format large numbers
export const formatLargeNumber = (num) => {
  if (!num && num !== 0) return "0";
  
  const number = typeof num === "string" ? parseFloat(num) : num;
  
  if (number >= 1000000) {
    return `${(number / 1000000).toFixed(1)}M`;
  } else if (number >= 1000) {
    return `${(number / 1000).toFixed(1)}K`;
  } else {
    return number.toLocaleString();
  }
};

// Get period options
export const getPeriodOptions = () => [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last year" },
];

// Validate date range
export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return start <= end;
}; 